import {
  alpha,
  filledInputClasses,
  inputLabelClasses,
  formHelperTextClasses
} from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { amber, blueGrey, red } from "../../../../mp-core/themes/palette"

type StylesParams = {
  inline: boolean
  label: boolean
  disabled: boolean
  error: boolean
  warning: boolean
  multiline?: boolean
  size: "small" | "normal"
  type: "outline" | "solid"
}

const getFocusState = (color: string) => {
  return {
    outline: "none",
    boxShadow: `0px 0px 0px 1px ${color}, 0px 0px 0px 3px ${alpha(color, 0.2)}`
  }
}

export const useStyles = makeStyles<StylesParams>()(
  (
    { spacing, transitions, palette },
    { inline, size, type, disabled, error, warning, label, multiline = false }
  ) => ({
    input: {
      width: "100%",

      [`& .${filledInputClasses.root}`]: {
        overflow: "hidden",
        borderRadius: "3px",
        border: `1px solid`,
        borderColor: type === "outline" ? blueGrey[200] : blueGrey[50],
        backgroundColor:
          type === "outline" ? palette.common.white : blueGrey[50],
        transition: transitions.create([
          "border-color",
          "background-color",
          "box-shadow"
        ]),

        "&:hover": {
          borderColor: type === "outline" ? blueGrey[400] : blueGrey[100],
          backgroundColor:
            type === "outline" ? palette.common.white : blueGrey[50]
        },

        [`&.${filledInputClasses.multiline}`]: {
          padding: 0
        },

        [`& .${filledInputClasses.input}`]: {
          color: disabled ? blueGrey[50] : blueGrey[800],
          padding: spacing(5, 4, 2, 4),
          height: "1.625rem",
          fontSize: "1rem",
          lineHeight: "1.375rem",

          ...(size === "small" && {
            padding: spacing(5, 3, 1, 4),
            height: "1.375rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem"
          }),

          ...(!label && {
            padding: spacing(4),
            height: "1.625rem",

            ...(size === "small" && {
              padding: spacing(4),
              height: "1.125rem"
            })
          }),

          ...(inline && {
            padding: spacing(2, 3),
            height: "1.125rem",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",

            ...(size === "small" && {
              padding: spacing(1, 2),
              height: "1.25rem"
            })
          }),

          ...(multiline && {
            paddingTop: 0,
            marginTop: spacing(5)
          }),

          "&::placeholder": {
            color: blueGrey[700],
            opacity: 1
          }
        },

        "&.Mui-focused": {
          ...getFocusState(palette.focus)
        },

        ...(disabled && {
          borderColor: blueGrey[50],
          backgroundColor:
            type === "outline" ? palette.common.white : blueGrey[50]
        }),

        ...(warning && {
          ...getFocusState(amber["A700"])
        }),

        ...(error && {
          ...getFocusState(red[400])
        })
      },

      [`& .${inputLabelClasses.root}`]: {
        color: disabled ? blueGrey[400] : blueGrey[500],
        left: "1px",
        transform: "translate(16px, 19px) scale(1)",

        [`&.${inputLabelClasses.filled}`]: {
          fontSize: "1rem",
          lineHeight: "1.375rem",

          ...(size === "small" && {
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            transform: "translate(16px, 16px) scale(1)"
          }),

          ...(inline && {
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
            transform: "translate(12px, 8px) scale(1)",

            ...(size === "small" && {
              transform: "translate(8px, 5px) scale(1)"
            })
          })
        },
        [`&.${inputLabelClasses.shrink}`]: {
          fontSize: "0.75rem",
          lineHeight: "0.875rem",
          transform: "translate(16px, 10px) scale(1)",

          ...(size === "small" && {
            transform: "translate(16px, 8px) scale(1)"
          }),

          ...(inline && { display: "none" })
        }
      },

      [`& .${inputLabelClasses.root}, & .${inputLabelClasses.asterisk}`]: {
        color: type === "outline" ? blueGrey[500] : blueGrey[600]
      },

      [`& .${formHelperTextClasses.root}`]: {
        margin: spacing(2, 4, 0),

        [`&.${formHelperTextClasses.error}`]: {
          color: red[700]
        }
      }
    }
  })
)
